/**
 * Settings Adapter
 *
 * SOLE mapping layer between BeHeard's data sources and the shared
 * `@levelup/ui-management/settings` section components.
 *
 * SettingsPageV2 is a pure UI composer: it imports these mappers, wires
 * them with raw inputs from hooks/Redux, and passes the resulting props
 * straight to the shared shell sections.
 *
 * RULES (do not violate):
 * - Pure functions only. No React state, no side effects.
 * - No data fetching here. Callers fetch; this layer reshapes only.
 * - Central is the SOLE authority for profile + workspace edits — the
 *   `onEdit*` callbacks built here open Central with returnUrl.
 * - Bearer-only. No cookies. No proxy.
 */

import type {
  ProfileSectionProps,
  WorkspaceSectionProps,
  BillingSectionProps,
  NotificationPreferencesSectionProps,
} from '@levelup/ui-management/settings';
import type { BillingState } from '@levelup/ui-management/billing';
import type {
  CategoryPreference,
  UpdateCategoryParams,
} from '@levelup/central-notifications';
import type {
  CentralRole,
  MembershipPermissions,
  WorkspaceMember,
  PendingInvite,
} from '@levelup/central-auth';
import type { SubscriptionStatus } from '@levelup/central-billing';
import {
  suggestSelectableRoles,
  suggestAssignableRoles,
} from '@levelup/central-context';

// ============================================================
// Types
// ============================================================

/** Subset of the Central user we need for ProfileSection. */
export interface CentralUserLike {
  id: string;
  email: string;
  fullName: string | null;
  avatarUrl: string | null;
}

/** Subset of the boot workspace we need for WorkspaceSection / Members. */
export interface BootWorkspaceLike {
  id: string;
  name: string;
  slug: string;
  type?: 'personal' | 'business';
  /** Central's workspace purpose (switch-context `purpose`: personal | business | client). */
  purpose?: string | null;
  role?: CentralRole | string;
  avatar?: { type: 'image' | 'initials'; imageUrl?: string; initials?: string } | null;
  permissions?: {
    manageMembers: boolean;
    manageBilling: boolean;
    transferOwnership: boolean;
  };
}

/** Raw billing facts pulled from BeHeard's RTK Query `getBillingStatus` + `getCredits`. */
export interface BillingFacts {
  planKey?: string;
  planName?: string;
  status?: string;
  currentPeriodEnd?: string | null;
  cancelAtPeriodEnd?: boolean;
  /** Central's stated credit balance; null = unknown (never shown as 0). */
  creditsTotal: number | null;
  /**
   * What Central said about a subscription: true = one is on file, false =
   * Central STATED there is none (the workspace is on the Free plan), absent =
   * not known. Only `false` produces the Free state; unknown stays unavailable.
   */
  hasSubscription?: boolean;
  isLoading: boolean;
  hasError: boolean;
  canManage: boolean;
  /** (billing contract §1 FINAL) Scheduled downgrade / non-renewal, as Central states it. */
  pendingChange?: { planCode: string; planName?: string | null; effectiveAt?: string | null; kind?: 'downgrade' | 'cancel_to_free' } | null;
  /** Seats in use vs the plan limit, as Central states them. */
  seats?: { used: number; limit: number | null } | null;
  /** Central's own billing permission for the caller (DB role); undefined = not stated. */
  centralCanManageBilling?: boolean;
}

/** Copy for a member who cannot manage billing (suite contract 1.2). */
export const ASK_OWNER_BILLING_COPY =
  'Billing for this workspace is managed by its owner or an admin. Ask your workspace owner to upgrade the plan or add credits.';

/** Notification fetch state — real Central state, not a fake empty array. */
export type NotificationsFetchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string }
  /** Central returned 200 but no preferences exist yet for this user. */
  | { status: 'not-configured' }
  | { status: 'ready'; preferences: CategoryPreference[] };

/** Raw inputs the Members tab needs from the page layer. */
export interface MembersFacts {
  workspaceId: string;
  currentRole: CentralRole;
  canManageMembers: boolean;
}

// ============================================================
// Profile mapping
// ============================================================

export interface ProfileAdapterInput {
  user: CentralUserLike | null;
  centralUrl: string;
  currentHref: string;
}

/**
 * The profile card's data. Name and avatar are edited in-app (`onSaveProfile`,
 * set by the page). There is deliberately NO Central "edit profile" hand-off:
 * Central has no personal profile page — `/settings/profile` renders workspace
 * settings.
 */
export function buildProfileSectionProps(
  input: ProfileAdapterInput,
): ProfileSectionProps {
  const { user } = input;
  return {
    avatarUrl: user?.avatarUrl ?? null,
    fullName: user?.fullName ?? null,
    email: user?.email ?? '',
  };
}

/**
 * "Change password": Central's reset flow, which works for every account
 * (Google-only ones get a password this way). `/auth/change-password` only
 * replaces an admin-issued temporary password. No `continue`: Central drops
 * absolute app URLs. Null without a Central URL — never a guessed link.
 */
export function centralChangePasswordUrl(centralUrl: string): string | null {
  const base = (centralUrl || '').replace(/\/+$/, '');
  return base ? `${base}/auth/forgot-password` : null;
}

// ============================================================
// Workspace mapping
// ============================================================

export interface WorkspaceAdapterInput {
  workspace: BootWorkspaceLike | null;
  canTransferOwnership: boolean;
  centralUrl: string;
  currentHref: string;
}

/**
 * Central's workspace PURPOSE when it stated a known one, else null. The shared
 * WorkspaceSection (ui-management ≥ 1.17) seeds its purpose field from `type`
 * and an owner's save always sends it — so it must be Central's purpose, never
 * Central's `type` (personal | team | enterprise) or a guess.
 */
export function workspacePurposeOf(workspace: BootWorkspaceLike | null): 'personal' | 'business' | 'client' | null {
  const p = workspace?.purpose;
  return p === 'personal' || p === 'business' || p === 'client' ? p : null;
}

export function buildWorkspaceSectionProps(
  input: WorkspaceAdapterInput,
): WorkspaceSectionProps {
  const { workspace, canTransferOwnership, centralUrl, currentHref } = input;
  const returnUrl = encodeURIComponent(currentHref);
  const wsAvatarUrl =
    workspace?.avatar?.type === 'image' && workspace.avatar.imageUrl
      ? workspace.avatar.imageUrl
      : null;

  // Central's workspace settings are addressed by workspace ID
  // (`/workspace/:workspaceId/settings/:tab`). These used the SLUG, and
  // `/transfer` / `/close` are not Central routes at all, so every button
  // opened a broken page. Rename, transfer of ownership and closing the
  // workspace all live on Central's General tab — a secure Central handoff
  // (owner-only actions stay in Central; contract §2).
  const workspaceGeneralPath = workspace?.id
    ? `/workspace/${encodeURIComponent(workspace.id)}/settings/general`
    : null;
  const openCentralWorkspaceSettings = () => {
    if (!centralUrl || !workspaceGeneralPath) return;
    window.open(`${centralUrl}${workspaceGeneralPath}?returnUrl=${returnUrl}`, '_blank');
  };

  return {
    name: workspace?.name ?? '',
    slug: workspace?.slug ?? '',
    type: workspacePurposeOf(workspace) ?? 'business',
    role: workspace?.role ?? 'viewer',
    avatarUrl: wsAvatarUrl,
    onEditWorkspace: openCentralWorkspaceSettings,
    onTransferOwnership: canTransferOwnership ? openCentralWorkspaceSettings : undefined,
    onDeleteWorkspace: canTransferOwnership ? openCentralWorkspaceSettings : undefined,
  };
}

// ============================================================
// Billing mapping
// ============================================================

export interface BillingAdapterInput {
  facts: BillingFacts;
  /** Manage an existing subscription (Central billing / Stripe portal). */
  onPortal: () => void;
  /** Upgrade: Central's plans page (contract 1.2). Defaults to onPortal. */
  onUpgrade?: () => void;
  /** Credits: Central's credits page (contract 1.2). Defaults to onPortal. */
  onCredits?: () => void;
  /** "Keep my current plan" — Central POST /api/billing/plan/change/cancel. */
  onCancelPendingChange?: () => void;
  /** Re-read plan + balance. */
  onRetry?: () => void;
}

/** True when Central STATED there is no subscription: the Free plan, not "unknown". */
export function isStatedFreePlan(facts: BillingFacts): boolean {
  return !facts.hasError && facts.hasSubscription === false;
}

export function buildBillingSectionProps(
  input: BillingAdapterInput,
): BillingSectionProps {
  const { facts, onPortal } = input;
  const onUpgrade = input.onUpgrade ?? onPortal;
  const onCredits = input.onCredits ?? onPortal;
  // Anything Central did not state is UNAVAILABLE — never a default of
  // 0 credits, plan "Free" or status "active". The section then shows the
  // reason in place of the figures (BillingSection replaces content on error).
  //
  // The ONE exception is a stated absence: Central answered "no subscription
  // on file" (404 / `subscription: null`). That is a real, valid state — the
  // workspace is on the Free plan — and it used to render as "Your plan is
  // unavailable", with no way to upgrade, for every workspace that had never
  // paid. It now renders as Free with an Upgrade action for those who may use it.
  const unavailable = billingUnavailableReason(facts);
  const free = isStatedFreePlan(facts);
  const status = (free ? 'active' : facts.status ?? 'unknown') as SubscriptionStatus;

  const billingState: BillingState = {
    plan: {
      planCode: free ? 'free' : facts.planKey ?? 'unavailable',
      planName: free ? 'Free' : facts.planName ?? 'Unavailable',
      status,
      currentPeriodEnd: free ? null : facts.currentPeriodEnd ?? null,
      cancelAtPeriodEnd: free ? false : facts.cancelAtPeriodEnd ?? false,
      trialEndsAt: null,
    },
    credits: {
      // Never rendered when unknown: `error` replaces the content.
      balance: facts.creditsTotal ?? Number.NaN,
    },
    billing: {
      available: facts.canManage,
      provider: 'stripe' as const,
    },
    upgradeAvailable: !unavailable && facts.canManage && status !== 'canceled',
  };

  return {
    billingState,
    onManageBilling: onPortal,
    // Members get no purchase button at all: the shared CreditBalanceCard renders
    // "Add Credits" only when a handler is given (`onAddCredits && <button>`),
    // although its props type declares it required. billingMemberNotice tells
    // members to ask the owner.
    onAddCredits: (facts.canManage ? onCredits : undefined) as BillingSectionProps['onAddCredits'],
    onUpgrade,
    isLoading: facts.isLoading,
    error: facts.isLoading ? null : unavailable,
    // ui-management ≥ 1.15 optional props (Central-stated only).
    canManageBilling: facts.centralCanManageBilling ?? facts.canManage,
    pendingChange: free ? null : facts.pendingChange ?? null,
    onCancelPendingChange: facts.canManage && facts.pendingChange ? input.onCancelPendingChange : undefined,
    billingCycle: 'month',
    seats: facts.seats ?? null,
    onRetry: input.onRetry,
  };
}

/**
 * Why the billing figures cannot be shown, or null when Central stated them
 * all. Unknown is said as such — it is never filled in with 0 / Free / active.
 * A STATED absence of a subscription is not unknown: it is the Free plan.
 */
export function billingUnavailableReason(facts: BillingFacts): string | null {
  if (facts.hasError) return 'Billing details are unavailable right now: Central could not be read. Try again shortly.';
  if (facts.creditsTotal === null) return 'Your credit balance is unavailable right now: Central did not report it.';
  if (isStatedFreePlan(facts)) return null;
  if (!facts.planName || !facts.status) return 'Your plan is unavailable: Central did not report it.';
  return null;
}

/**
 * The notice shown to a member who cannot manage billing, or null for owners /
 * admins and while loading. Members are never handed a billing button that
 * leads to a page they cannot use.
 */
export function billingMemberNotice(facts: BillingFacts): string | null {
  if (facts.isLoading || facts.canManage) return null;
  return ASK_OWNER_BILLING_COPY;
}

// ============================================================
// Members mapping
// ============================================================

export interface MembersPermissionsAdapterInput {
  currentRole: CentralRole;
  canManageMembers: boolean;
}

/**
 * Build the canonical MembershipPermissions object the shared
 * MembershipList / InviteMemberModal / PendingInvitesList expect.
 */
export function buildMembershipPermissions(
  input: MembersPermissionsAdapterInput,
): MembershipPermissions {
  const { currentRole, canManageMembers } = input;
  return {
    canInvite: canManageMembers,
    canRemoveMembers: canManageMembers,
    // D1: only the OWNER changes workspace roles — Central refuses an admin
    // (PATCH /members/:userId), so an admin is never offered the role selector.
    canChangeRoles: canManageMembers && currentRole === 'owner',
    canCancelInvites: canManageMembers,
    selectableRoles: suggestSelectableRoles(currentRole),
    assignableRoles: suggestAssignableRoles(currentRole),
  };
}

/** Pass-through bag of props the MembersTab UI consumes. */
export interface MembersViewModel {
  permissions: MembershipPermissions;
  members: WorkspaceMember[];
  invites: PendingInvite[];
  isLoadingMembers: boolean;
  isLoadingInvites: boolean;
  membersError: string | null;
  invitesError: string | null;
}

// ============================================================
// Notifications mapping
// ============================================================

export interface NotificationsAdapterInput {
  state: NotificationsFetchState;
  onUpdateCategory: (params: UpdateCategoryParams) => void;
}

/**
 * Map the real Central fetch state into NotificationPreferencesSection props.
 *
 * - `loading`         → `isLoading: true`
 * - `error`           → `error: <message>`           (real failure)
 * - `not-configured`  → `preferences: []`, `error: null`, `isLoading: false`
 *                       (valid empty state — the page renders a neutral
 *                       informational banner above the section explaining
 *                       that no categories have been configured yet)
 * - `ready`           → live preferences
 *
 * The page is responsible for rendering the "no categories yet" notice
 * because it is a UX hint, not an error — the shared section MUST NOT
 * be told to show error styling for a valid empty server response.
 */
export function buildNotificationsSectionProps(
  input: NotificationsAdapterInput,
): NotificationPreferencesSectionProps {
  const { state, onUpdateCategory } = input;

  const handleUpdate: NotificationPreferencesSectionProps['onUpdateCategory'] = (
    category,
    channel,
    enabled,
  ) => {
    onUpdateCategory({
      category,
      channels: { [channel]: enabled } as Partial<
        Record<'inbox' | 'push' | 'email', boolean>
      >,
    });
  };

  switch (state.status) {
    case 'idle':
    case 'loading':
      return {
        preferences: [],
        onUpdateCategory: handleUpdate,
        isLoading: true,
        error: null,
      };
    case 'error':
      return {
        preferences: [],
        onUpdateCategory: handleUpdate,
        isLoading: false,
        error: state.message,
      };
    case 'not-configured':
      return {
        preferences: [],
        onUpdateCategory: handleUpdate,
        isLoading: false,
        error: null,
      };
    case 'ready':
      return {
        preferences: state.preferences,
        onUpdateCategory: handleUpdate,
        isLoading: false,
        error: null,
      };
  }
}

/**
 * Helper for the page layer: should we render a neutral info banner above
 * the preferences section? True only when Central confirmed there are no
 * categories yet (NOT during loading and NOT on real errors).
 */
export function isNotificationsNotConfigured(state: NotificationsFetchState): boolean {
  return state.status === 'not-configured';
}
